import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, inject, Input, TemplateRef, viewChild } from '@angular/core';
import { popoverAnimation } from '@shared/animations/popover.animation';
import { ConnectionPositionPair, OverlayModule } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { NgTemplateOutlet } from '@angular/common';

export enum PopoverSectionType {
	TEXT = 'text',
	LIST = 'list',
	TABLE = 'table',
}

/**
 * Represents a section within a popover component.
 *
 * A `PopoverSection` is used to define a segment inside a popover, categorizing or grouping
 * related items for better organization and presentation.
 *
 * @interface PopoverSection
 * @property {PopoverSectionType} type - Specifies the type of the section, controlling its visual style or behavior.
 * @property {string[]} items - List of strings representing the individual items contained within this section.
 */
export interface PopoverSection {
	type: PopoverSectionType;
	items?: string[];
	headers?: string[];
	rows?: PopoverTableCell[][];
}

export type PopoverIconState = 'active' | 'inactive' | 'info';

export interface PopoverIconToken {
	icon: string;
	state?: PopoverIconState;
	fontSet?: string;
}

export interface PopoverIconCell {
	kind: 'icons';
	icons: PopoverIconToken[];
}

export type PopoverTableCell = string | PopoverIconCell;

/**
 * Represents the content structure for a popover component.
 *
 * This interface defines the primary layout and hierarchies of sections
 * that can be included within a popover. Each popover contains a list of
 * sections that provide the necessary content to display.
 *
 * @interface
 */
export interface PopoverContent {
	sections: PopoverSection[];
}

export type PopoverInput = PopoverContent | string | null | undefined;

/**
 * A component for displaying a popover UI element, which typically appears as a floating box
 * anchored to a trigger element, such as an icon or button. The popover can show contextual information
 * or actions based on the provided content.
 *
 * ### Inputs
 * `content` (PopoverContent | undefined):
 *   The content to be displayed in the popover. It can include multiple sections or be empty/null. It is structured into an Array of `PopoverSection` objects.
 *   Each section can be of a specific type, which determines its visual appearance and behavior.
 */
@Component({
	selector: 'ara-popover',
	imports: [OverlayModule, MatIcon, NgTemplateOutlet],
	templateUrl: './popover.component.html',
	styleUrl: './popover.component.scss',
	animations: [popoverAnimation],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AraPopoverComponent {
	private readonly cdr = inject(ChangeDetectorRef);
	private _content?: PopoverInput;
	private _sections: PopoverSection[] = [];
	protected readonly PopoverSectionType = PopoverSectionType;
	private readonly triggerElement = viewChild<ElementRef<HTMLElement>>('triggerElement');
	private readonly panelElement = viewChild<ElementRef<HTMLElement>>('panelElement');
	@Input() visibleOnTruncate = false;
	@Input()
	set showOnlyIfTruncated(value: boolean) {
		this.visibleOnTruncate = value;
	}

	/**
	 * Optional custom panel content. When set, the panel renders this template instead of the structured
	 * `content` sections, and the wrapper drops its card chrome so the projected content supplies its own.
	 */
	@Input() panelTemplate?: TemplateRef<unknown>;

	// Delay closing so the pointer can move from the trigger (e.g., info icon) to the popover.
	private closeTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly closeDelayMs = 150;
	// Auto-close safety net: tracks mouse position while open, closes when pointer leaves trigger+panel
	private mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
	private lastMouseX = 0;
	private lastMouseY = 0;
	private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

	isOpen = false;
	readonly positions: ConnectionPositionPair[] = [
		{
			originX: 'center',
			originY: 'top',
			overlayX: 'center',
			overlayY: 'bottom',
			offsetY: -4,
		},
		{
			originX: 'center',
			originY: 'bottom',
			overlayX: 'center',
			overlayY: 'top',
			offsetY: 4,
		},
	];

	@Input()
	set content(value: PopoverInput) {
		this._content = value;
		this._sections = this.parseContent(value);
		if (!this.hasContent) {
			this.forceClose();
		}
	}

	get content(): PopoverInput {
		return this._content;
	}

	get sections(): PopoverSection[] {
		return this._sections;
	}

	get hasContent(): boolean {
		if (this.panelTemplate) {
			return true;
		}
		return this._sections.some((section) => {
			if (section.type === PopoverSectionType.TABLE) {
				const hasHeaders = (section.headers ?? []).some((header) => !!header?.trim());
				const hasRows = (section.rows ?? []).some((row) => row.some((cell) => this.hasTableCellContent(cell)));
				return hasHeaders || hasRows;
			}
			return (section.items ?? []).some((item) => !!item?.trim());
		});
	}

	isIconCell(cell: PopoverTableCell): cell is PopoverIconCell {
		return !!cell && typeof cell !== 'string' && cell.kind === 'icons';
	}

	iconStateClass(state?: PopoverIconState): string {
		switch (state) {
			case 'active':
				return 'popover-icon-active';
			case 'info':
				return 'popover-icon-info';
			case 'inactive':
			default:
				return 'popover-icon-inactive';
		}
	}

	private hasTableCellContent(cell: PopoverTableCell): boolean {
		if (typeof cell === 'string') {
			return !!cell.trim();
		}
		return (cell.icons ?? []).length > 0;
	}

	show() {
		if (!this.hasContent) {
			this.forceClose();
			return;
		}
		if (!this.canShowPopover()) {
			this.forceClose();
			return;
		}
		this.cancelScheduledClose();
		// If pointer already left trigger before overlay rendered, don't open
		if (this.mouseMoveHandler && !this.isPointInElements(this.lastMouseX, this.lastMouseY)) {
			this.forceClose();
			return;
		}
		this.isOpen = true;
		this.startAutoCloseCheck();
	}

	hide() {
		this.scheduleClose();
	}

	/**
	 * Opens the popover programmatically (e.g. a first-visit hint), skipping the pointer-based
	 * auto-close so it stays open until an outside click or an explicit forceClose().
	 */
	openPinned(): void {
		if (!this.hasContent) return;
		this.cancelScheduledClose();
		this.isOpen = true;
		this.cdr.markForCheck();
	}

	forceClose(): void {
		this.cancelScheduledClose();
		this.stopAutoCloseCheck();
		this.isOpen = false;
		this.cdr.markForCheck();
	}

	@HostListener('window:blur')
	onWindowBlur(): void {
		this.forceClose();
	}

	private parseContent(content?: PopoverInput): PopoverSection[] {
		if (typeof content === 'string') {
			const text = content.trim();
			if (!text) {
				return [];
			}
			return [{ type: PopoverSectionType.TEXT, items: [text] }];
		}
		return content?.sections ?? [];
	}

	private scheduleClose(): void {
		this.cancelScheduledClose();
		this.closeTimer = setTimeout(() => {
			this.isOpen = false;
			this.closeTimer = null;
			this.cdr.markForCheck();
		}, this.closeDelayMs);
	}

	private cancelScheduledClose(): void {
		if (this.closeTimer !== null) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
	}

	private startAutoCloseCheck(): void {
		this.stopAutoCloseCheck();
		this.mouseMoveHandler = (e: MouseEvent) => {
			this.lastMouseX = e.clientX;
			this.lastMouseY = e.clientY;
		};
		document.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });
		// Delayed rect-check: gives the panel time to render before first check
		this.scheduleAutoCloseCheck(100);
	}

	private scheduleAutoCloseCheck(delayMs: number): void {
		this.autoCloseTimer = setTimeout(() => {
			if (!this.isOpen) {
				this.stopAutoCloseCheck();
				return;
			}
			if (this.isPointInElements(this.lastMouseX, this.lastMouseY)) {
				this.scheduleAutoCloseCheck(100);
			} else {
				this.forceClose();
			}
		}, delayMs);
	}

	private isPointInElements(x: number, y: number): boolean {
		const pad = 4; // small tolerance
		const triggerRect = this.triggerElement()?.nativeElement?.getBoundingClientRect();
		const panelRect = this.panelElement()?.nativeElement?.getBoundingClientRect();
		return (
			this.isPointInRect(x, y, triggerRect, pad) || this.isPointInRect(x, y, panelRect, pad) || this.isPointInBridge(x, y, triggerRect, panelRect, pad)
		);
	}

	/** Check if the pointer is in the gap between trigger and panel (bridge zone). */
	private isPointInBridge(x: number, y: number, a?: DOMRect, b?: DOMRect, pad = 0): boolean {
		if (!a || !b) return false;
		const left = Math.min(a.left, b.left) - pad;
		const right = Math.max(a.right, b.right) + pad;
		const top = Math.min(a.bottom, b.bottom) - pad;
		const bottom = Math.max(a.top, b.top) + pad;
		if (top >= bottom) return false; // no gap
		return x >= left && x <= right && y >= top && y <= bottom;
	}

	private isPointInRect(x: number, y: number, rect?: DOMRect, pad = 0): boolean {
		if (!rect) return false;
		return x >= rect.left - pad && x <= rect.right + pad && y >= rect.top - pad && y <= rect.bottom + pad;
	}

	private stopAutoCloseCheck(): void {
		if (this.autoCloseTimer !== null) {
			clearTimeout(this.autoCloseTimer);
			this.autoCloseTimer = null;
		}
		if (this.mouseMoveHandler) {
			document.removeEventListener('mousemove', this.mouseMoveHandler);
			this.mouseMoveHandler = null;
		}
	}

	private canShowPopover(): boolean {
		if (!this.visibleOnTruncate) {
			return true;
		}

		const target = this.getTruncationTarget();
		if (!target) {
			return false;
		}

		return target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight;
	}

	private getTruncationTarget(): HTMLElement | null {
		const trigger = this.triggerElement()?.nativeElement;
		if (!trigger) {
			return null;
		}

		const explicitTarget = trigger.querySelector<HTMLElement>('[data-popover-truncate-target]');
		if (explicitTarget) {
			return explicitTarget;
		}

		return (trigger.firstElementChild as HTMLElement | null) ?? trigger;
	}
}
